import Link from 'next/link';

import { sizeClasses } from '../../styles/sizeClasses';
import { loaderTheme, themeClasses } from '../../styles/themeClasses';
import { tw } from '../../utils';
import { Loader } from '../Loader';

export interface ButtonProps {
  size?: 'small' | 'medium' | 'large';
  theme?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'rainbow'
    | 'doodlesTV'
    | 'retroGlow'
    | 'claim';
  fullWidth?: boolean;
  htmlType?: 'button' | 'submit' | 'reset';
  as?: 'button' | 'a' | 'nextLink';
  target?: '_parent' | '_blank' | '_self';
  href?: string;
  onClick?: (e?: any) => void;
  isLoading?: boolean;
  disabled?: boolean;
  scroll?: boolean;
  extraClasses?: string;
  themeOverrides?: {
    [key: string]: string | number;
  };
  children: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({
  size = 'small',
  theme = 'primary',
  fullWidth = true,
  htmlType = 'button',
  as: Component = 'button',
  target,
  href,
  onClick,
  isLoading,
  disabled,
  extraClasses,
  themeOverrides,
  children,
  scroll = true,
  ...props
}: ButtonProps) => {
  const additionalProps =
    Component === 'button'
      ? { type: htmlType }
      : Component === 'a'
        ? { href, target }
        : {};

  const buttonClasses = tw(
    `${sizeClasses[size]}`,
    `${themeClasses[theme]}`,
    `${fullWidth ? 'w-full' : 'w-max'}`,
    'flex items-center justify-center gap-2',
    'font-medium font-fredoka tracking-wide',
    'disabled:opacity-50 disabled:hover:cursor-not-allowed',
    `${extraClasses ?? null}`,
  );

  if (Component === 'nextLink') {
    if (href) {
      return (
        <Link
          href={href}
          scroll={scroll}
          target={target ? target : '_self'}
          className={tw(fullWidth ? 'w-full' : 'w-max')}
        >
          <button
            className={buttonClasses}
            disabled={disabled ?? isLoading}
            {...props}
            style={themeOverrides}
          >
            {children}
          </button>
        </Link>
      );
    } else {
      return children;
    }
  }

  return (
    <Component
      onClick={onClick}
      className={buttonClasses}
      disabled={disabled ?? isLoading}
      {...additionalProps}
      {...props}
      style={themeOverrides}
    >
      {isLoading ? (
        <Loader
          size={size}
          theme={loaderTheme[theme]}
        />
      ) : null}
      {children}
    </Component>
  );
};
